import {
	DialogController,
	PredefinedDialog,
	PredefinedDialogType,
} from '~/components/passporterUi/Dialog/dialogController'
import { Destination2 } from '~/models/Destination'
import { Itinerary2, TripOptions } from '~/models/Itinerary'
import ItineraryGroup from '~/models/ItineraryGroup'
import { DestinationService } from '~/passporter-services/destination/service'
import { ItineraryService } from '~/passporter-services/itinerary/service'
import Calendar from '~/passporter-services/shared/calendar'
import { useItineraryStore } from '~/store/itinerary'
import AddDatesDialog from '~/components/shared/AddDatesDialog.vue'
import EventsService from '~/services/events'
import { TranslationService } from '~/services/Translation'
import { useProfileTrips } from '~/store/profileTrips'

export class ItineraryActions {
	private static get itineraryStore() {
		return useItineraryStore()
	}

	static async getDestinations(itineraryId: string): Promise<Destination2[]> {
		try {
			const stored: Destination2[] | undefined =
				this.itineraryStore.retrieveDestinations(itineraryId)
			if (stored) return stored

			const destinations = await ItineraryService.getDestinations(itineraryId)
			this.itineraryStore.addDestinationsTo(itineraryId, destinations)
			return destinations
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static async getTripOptions(itineraryId: string): Promise<TripOptions | null> {
		try {
			return await ItineraryService.getTripOptions(itineraryId)
		} catch {
			return null
		}
	}

	static async getItinerary(itineraryId: string): Promise<Itinerary2> {
		try {
			const stored: Itinerary2 | undefined = this.itineraryStore.retrieveItinerary(itineraryId)
			if (stored) return stored
			let itinerary = await ItineraryService.getItinerary(itineraryId)
			const options = await ItineraryService.getTripOptions(itineraryId)
			itinerary = { ...options, ...itinerary }
			this.itineraryStore.addItinerary(itinerary)
			return itinerary
		} catch (e) {
			console.error(e)
			return {} as Itinerary2
		}
	}

	static async getInvitationTrip(
		tripId: string,
		invitationCode: string
	): Promise<Itinerary2 | undefined> {
		try {
			return await ItineraryService.getInvitationTrip(tripId, invitationCode)
		} catch (e) {
			console.error(e)
			return
		}
	}

	static async resetItinerary(itineraryId: string): Promise<Itinerary2> {
		try {
			const itinerary = await ItineraryService.getItinerary(itineraryId)
			this.itineraryStore.removeItinerary(itinerary.id!)
			this.itineraryStore.addItinerary(itinerary)
			return itinerary
		} catch (e) {
			console.error(e)
			return {} as Itinerary2
		}
	}

	static async editDates(itineraryId: string, dates: Date[]): Promise<Itinerary2> {
		const translation: TranslationService = TranslationService.getInstance()
		try {
			const oldItinerary: Itinerary2 = await this.getItinerary(itineraryId)
			const withNewDates: Itinerary2 = {
				...oldItinerary,
				startDate: Calendar.getDateString(dates[0]!),
				endDate: Calendar.getDateString(dates[1]!),
			}
			await this.editItinerary(withNewDates)
			EventsService.emitSuccess(translation.translate('web.itinerary.toast.dates_updated_success'))
			this.profileTripsStore().resetLastUpdatedTrip()
			return this.resetItinerary(itineraryId)
		} catch (e) {
			console.error(e)
			EventsService.emitError(translation.translate('web.itinerary.toast.dates_update_error'))
			return {} as Itinerary2
		}
	}

	static async editItinerary(newItinerary: Itinerary2): Promise<Itinerary2> {
		try {
			await ItineraryService.editItinerary(newItinerary)
			const itinerary = this.resetItinerary(newItinerary.id!)
			return itinerary
		} catch (e) {
			console.error(e)
			return {} as Itinerary2
		}
	}

	static async getDestinationStamp(itineraryId: string): Promise<string> {
		try {
			const destinations: Destination2[] = await ItineraryService.getDestinations(itineraryId)
			return await DestinationService.getStamp(destinations[0]!.id)
		} catch {
			return ''
		}
	}

	static async getGroups(itineraryId: string): Promise<ItineraryGroup[]> {
		try {
			return await ItineraryService.getGroups(itineraryId)
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static createAddDatesDialog(dialogTitle: string) {
		const deleteDialog = new PredefinedDialog(PredefinedDialogType.EMPTY, AddDatesDialog)
			.title(dialogTitle)
			.size('s')
			.mobileType('fullScreen')
		return new DialogController(deleteDialog.build())
	}

	private static profileTripsStore() {
		return useProfileTrips()
	}
}
